import type { Scene } from 'three';
import type { MedalTimes } from '../game/medals';
import { buildArcaneAcademy } from './ArcaneAcademy';
import { buildExpanse } from './Expanse';
import type { ScenePalette } from './environment';
import { buildFallLine, fallLineMedals, fallLinePalette } from './FallLine';
import { buildProvingGrounds, provingGroundsPalette } from './ProvingGrounds';
import { buildSpillway, spillwayPalette } from './Spillway';
import { type BuiltLevel, buildTestLevel } from './TestLevel';

/** Deep-indigo night void for surf_arcane_academy — a clean dark sky (plus a
 * starfield in the map) so the glowing crystal academy + bloom pop. A flat
 * background skips the HDRI entirely (cheaper) and avoids horizon artifacts. */
const arcaneAcademyPalette: ScenePalette = {
  background: 0x0b0a1e, // midnight indigo void
  fogColor: 0x0b0a1e,
  fogDensity: 0.00004, // light — distant ramps must stay visible (readability)
  sunColor: 0xbcd0ff, // cool moonlight key (brighter → ramps read against the void)
  sunIntensity: 2.2, // lifts non-emissive surfaces against the dark void
  envIntensity: 0.95, // hemisphere fill (flat-bg path)
  ambientColor: 0x3c4178,
};

export type Tier = 1 | 2 | 3 | 4;

export interface MapDef {
  id: string;
  name: string;
  load: (scene: Scene) => Promise<BuiltLevel>;
  /** Difficulty tier (timed maps). Omitted for the untimed Playground. */
  tier?: Tier;
  /** Medal time targets (ticks). Omitted for untimed maps (no zones). */
  medals?: MedalTimes;
  /** Per-map atmosphere; omitted → default golden-hour HDRI. */
  palette?: ScenePalette;
  /** Force bloom even on the 'low' preset (for emissive/glowy maps). */
  forceBloom?: boolean;
}

/**
 * All playable maps. The surf maps were retired to focus on the Playground
 * (owner directive); the map framework (mapkit/buildMap/exporter/playtest) is
 * kept dormant so authored maps can return later. `MapDef`'s tier/medals/palette
 * fields stay so re-adding a timed map is a one-entry change.
 */
export const MAPS: MapDef[] = [
  {
    id: 'arena_proving_grounds',
    name: 'The Proving Grounds',
    load: async (scene) => buildProvingGrounds(scene),
    palette: provingGroundsPalette,
    // The first combat arena (spec 0026): surf the lane, fight the dummies.
  },
  {
    id: 'gym_falline',
    name: 'The Playground',
    load: async (scene) => buildTestLevel(scene),
  },
  {
    id: 'surf_arcane_academy',
    name: 'Arcane Academy',
    load: async (scene) => buildArcaneAcademy(scene),
    palette: arcaneAcademyPalette,
    forceBloom: true, // glowing crystal/runes should bloom even on low
  },
  {
    id: 'surf_expanse',
    name: 'The Expanse',
    load: async (scene) => buildExpanse(scene),
    // Plain theme + default sky; untimed free-surf, 500-shape generated, many lanes.
  },
  {
    id: 'surf_spillway',
    name: 'The Spillway',
    load: async (scene) => buildSpillway(scene),
    palette: spillwayPalette,
    // Untimed free-surf: a flowing slalom descent distilled from the CS surf canon.
  },
  {
    id: 'surf_fall_line',
    name: 'The Fall Line',
    load: async (scene) => buildFallLine(scene),
    tier: 4, // the namesake timed course: an 8-pitch T1→T4 alpine descent
    medals: fallLineMedals,
    palette: fallLinePalette,
  },
];

export function resolveMap(id: string | null): MapDef {
  const fallback = MAPS[0];
  if (!fallback) {
    throw new Error('No maps registered');
  }
  return MAPS.find((m) => m.id === id) ?? fallback;
}
